import * as React from "react";
import classnames from "classnames";
import { Breadcrumbs } from "@grandvisionhq/common";
import { Text } from "@grandvisionhq/elements";
import { Types } from "@grandvisionhq/graphql";
import { Alert } from "@grandvisionhq/iris-ui";
import { Location, Types as Types$1, OpeningHours } from "@grandvisionhq/locations";
import { useIntl, useFeatureToggles } from "@grandvisionhq/state";
import { areServicesAvailable } from "../../../../utils/utils/store-utils.js";
/* empty css                      */
import useIsInViewport from "../../../../hooks/hooks/useIsInViewport.js";
import { getActiveStoreOffers } from "../../../../utils/utils/get-active-store-offers.js";
import { getActiveCampaignBanner } from "../../../../utils/utils/get-active-campaign-banner.js";
import usePreferredStore from "../../../../hooks/hooks/use-preferred-store.js";
import StoreDistance from "../../../../components/StoreDistance/components/StoreDistance/StoreDistanceContainer.js";
import SimpleBreadcrumb from "../../../../components/SimpleBreadcrumb/components/SimpleBreadcrumb/SimpleBreadcrumb.js";
import StoreDetailImageContainer from "../../../../components/StoreDetailImage/components/StoreDetailImage/StoreDetailImageContainer.js";
import StoreBookAppointmentCTAContainer from "../../../../components/StoreBookAppointmentCTA/components/StoreBookAppointmentCTA/StoreBookAppointmentCTAContainer.js";
import StoreLocationContainer from "../../../../components/StoreLocation/components/StoreLocation/StoreLocationContainer.js";
import StoreFacilitiesContainer from "../../../../components/StoreFacilities/components/StoreFacilities/StoreFacilitiesContainer.js";
import { StoreRatingContainer } from "../../../../components/StoreRating/components/StoreRating/StoreRatingContainer.js";
import StoreOffersContainer from "../../../../components/StoreOffers/components/StoreOffers/StoreOffersContainer.js";
import { NearbyStoresContainer } from "../../../../components/NearbyStores/components/NearbyStores/NearbyStoresContainer.js";
import StoreCategoriesContainer from "../../../../components/StoreCategories/components/StoreCategories/StoreCategoriesContainer.js";
import StoreDescriptionContainer from "../../../../components/StoreDescription/components/StoreDescription/StoreDescriptionContainer.js";
import StoreFranchiseIndicatorContainer from "../../../../components/StoreFranchiseIndicator/components/StoreFranchiseIndicator/StoreFranchiseIndicatorContainer.js";
import StoreOpticiansContainer from "../../../../components/StoreOpticians/components/StoreOpticians/StoreOpticiansContainer.js";
import StoreBrandContainer from "../../../../components/StoreBrands/components/StoreBrands/StoreBrandsContainer.js";
import StorePaymentMethodsContainer from "../../../../components/StorePaymentMethods/components/StorePaymentMethods/StorePaymentMethodsContainer.js";
import StoreSocialMediaContainer from "../../../../components/StoreSocialMedia/components/StoreSocialMedia/StoreSocialMediaContainer.js";
const StoreDetailsRoot = ({
  store,
  opco,
  hidePhone,
  hideEmail,
  breadcrumbs,
  urls,
  appointmentTypeId,
  logos,
  dropInAppointmentBookingWidget: DropInWidget = null,
  nearestStores = [],
  campaignBanner,
  productCategories,
  preferredStoreButton
}) => {
  const { getFormattedLabel } = useIntl();
  const { featureConfigs: { storeDistanceConfig } = {} } = useFeatureToggles();
  const canShowBookAppointmentWidget = areServicesAvailable(store, [Types.ServicesNotAvailable.APPOINTMENTS]) && Boolean(urls.bookAppointment?.length);
  const showBookAppointmentDropInWidget = canShowBookAppointmentWidget && Boolean(appointmentTypeId?.length) && DropInWidget;
  const showBookAppointmentCTA = canShowBookAppointmentWidget && !showBookAppointmentDropInWidget;
  const {
    name,
    code,
    globalStoreId,
    shortName,
    description,
    detailImage,
    emailAddress,
    phone,
    streetName,
    streetNumber,
    postalCode,
    town,
    province,
    country,
    additionalStreetInfo,
    lat,
    lon,
    storeNote,
    openingHours,
    specialOpeningHours,
    facilities,
    brands,
    paymentMethods,
    socialMediaLinks,
    opticians,
    offers,
    wheelchairAccessible,
    storeProductCategories,
    hearingAppointmentPossible,
    isFranchiseStore
  } = store;
  const bookAppointmentButtonRef = React.useRef(null);
  const dropInWidgetRef = React.useRef(null);
  const isBookAppointmentButtonInViewport = useIsInViewport(bookAppointmentButtonRef);
  const isDropInWidgetInViewport = useIsInViewport(dropInWidgetRef);
  const storeCategories = getActiveStoreOffers(productCategories, storeProductCategories);
  const activeCampaignBanner = getActiveCampaignBanner(campaignBanner, {
    globalStoreId
  });
  const { preferredStoreUpdated, preferredStoreSuccess, preferredStoreAlertLabel } = usePreferredStore(shortName ?? name);
  const hideStickyBookAppointmentButton = isBookAppointmentButtonInViewport || isDropInWidgetInViewport;
  const showPaymentMethods = !!paymentMethods && !!logos?.paymentMethods && paymentMethods.length > 0 && logos.paymentMethods.length > 0;
  const showSocialMedia = !!socialMediaLinks && !!logos?.socialMedia && socialMediaLinks.length > 0 && logos.socialMedia.length > 0;
  const addressData = React.useMemo(
    () => ({
      additionalStreetInfo,
      streetName,
      streetNumber,
      postalCode,
      town,
      province
    }),
    [additionalStreetInfo, streetName, streetNumber, postalCode, town, province]
  );
  const distanceDetails = React.useMemo(
    () => storeDistanceConfig ? /* @__PURE__ */ React.createElement(StoreDistance, { globalStoreId }) : null,
    [globalStoreId, storeDistanceConfig]
  );
  const labels = React.useMemo(() => {
    const label = getFormattedLabel("stores.storeDetailsRoot.announcement", {
      hideLabelKey: true,
      parseLink: true
    });
    if (!label) {
      return {};
    }
    return {
      storeAnnouncement: label
    };
  }, [getFormattedLabel]);
  return /* @__PURE__ */ React.createElement("article", { className: "store-details-stores-v2" }, /* @__PURE__ */ React.createElement("div", { className: "store-details-stores-v2__top-wrapper" }, preferredStoreUpdated ? /* @__PURE__ */ React.createElement(
    Alert,
    {
      variant: preferredStoreSuccess ? "success" : "warning",
      className: "alert-m-default"
    },
    preferredStoreAlertLabel
  ) : null, /* @__PURE__ */ React.createElement("div", { className: "store-details-stores-v2__top-row" }, breadcrumbs && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("div", { className: "store-details-stores-v2__breadcrumbs" }, /* @__PURE__ */ React.createElement(Breadcrumbs, { pages: breadcrumbs })), breadcrumbs.length >= 2 && /* @__PURE__ */ React.createElement("div", { className: "store-details-stores-v2__breadcrumb" }, /* @__PURE__ */ React.createElement(SimpleBreadcrumb, { title: breadcrumbs[1]?.name ?? "", href: breadcrumbs[1]?.url }))), preferredStoreButton ? preferredStoreButton : null)), /* @__PURE__ */ React.createElement("div", { className: "store-details-stores-v2__content-wrapper" }, /* @__PURE__ */ React.createElement("div", { className: "store-details-stores-v2__main-content-wrapper" }, /* @__PURE__ */ React.createElement("div", { className: "store-details-stores-v2__main-content" }, /* @__PURE__ */ React.createElement(
    StoreDetailImageContainer,
    {
      className: "store-details-stores-v2__detail-image",
      detailImageUrl: detailImage,
      fallbackImage: urls.storeFallbackImage,
      alt: shortName || name
    }
  ), /* @__PURE__ */ React.createElement("div", { className: "store-details-stores-v2__contact-info" }, /* @__PURE__ */ React.createElement(
    Location,
    {
      className: "store-details-stores-v2__location",
      id: globalStoreId,
      name,
      variant: Types$1.LocationVariant.StandAlone,
      emailAddress,
      phone,
      emailHidden: hideEmail,
      phoneHidden: hidePhone,
      locationDetailsUrl: "",
      headerImage: activeCampaignBanner,
      highlight: false,
      addressData,
      addressSize: "COMPLETE",
      wheelchairAccessible,
      hearingAppointmentPossible,
      isFranchiseStore,
      distanceDetails,
      openingHours: /* @__PURE__ */ React.createElement(
        OpeningHours,
        {
          withTitle: true,
          announcement: storeNote,
          openingHours,
          specialOpeningHours,
          isExpandable: false,
          isExpanded: true
        }
      ),
      buttons: /* @__PURE__ */ React.createElement(React.Fragment, null, showBookAppointmentDropInWidget ? /* @__PURE__ */ React.createElement(
        "div",
        {
          className: "store-details-stores-v2__drop-in-widget-container",
          ref: dropInWidgetRef
        },
        /* @__PURE__ */ React.createElement(
          DropInWidget,
          {
            urls,
            storeId: globalStoreId,
            typeId: appointmentTypeId,
            dataLayerEvents: {
              storeAppointmentClickEvent: "storeDetailsFastAppointmentClick",
              storeChangeAppointmentTypeClickEvent: "storeDetailsChangeClick"
            }
          }
        )
      ) : null, showBookAppointmentCTA ? /* @__PURE__ */ React.createElement(
        "div",
        {
          ref: bookAppointmentButtonRef,
          className: "store-details-stores-v2__book-appointment"
        },
        /* @__PURE__ */ React.createElement(
          StoreBookAppointmentCTAContainer,
          {
            url: urls.bookAppointment ?? "",
            globalStoreId,
            code,
            name,
            town,
            country,
            streetName,
            streetNumber,
            additionalStreetInfo,
            postalCode,
            appointmentTypeId
          }
        )
      ) : null, (showBookAppointmentDropInWidget || showBookAppointmentCTA) && labels.storeAnnouncement && /* @__PURE__ */ React.createElement(Text, { variant: "caption", children: labels.storeAnnouncement }))
    }
  ))), /* @__PURE__ */ React.createElement("div", { className: "store-details-stores-v2__secondary-content" }, /* @__PURE__ */ React.createElement("div", { className: "store-details-stores-v2__location" }, /* @__PURE__ */ React.createElement(
    StoreLocationContainer,
    {
      opco,
      globalStoreId,
      name,
      town,
      province,
      country,
      streetName,
      streetNumber,
      additionalStreetInfo,
      postalCode,
      lat,
      lon,
      distanceDetails,
      practicalInformation: store.practicalInformation
    }
  )), facilities && facilities.length > 0 && /* @__PURE__ */ React.createElement("div", { className: "store-details-stores-v2__facilities" }, /* @__PURE__ */ React.createElement(StoreFacilitiesContainer, { facilities })), /* @__PURE__ */ React.createElement(StoreRatingContainer, { store }), offers && offers.length > 0 && /* @__PURE__ */ React.createElement(
    StoreOffersContainer,
    {
      className: "store-details-stores-v2__store-offers",
      fallbackImage: urls.offerFallbackImage,
      offers
    }
  ), nearestStores.length > 0 && /* @__PURE__ */ React.createElement(
    NearbyStoresContainer,
    {
      className: "store-details-stores-v2__nearest-stores",
      stores: nearestStores,
      urls,
      storeDetails: store
    }
  ), storeCategories && storeCategories.length > 0 && /* @__PURE__ */ React.createElement(
    StoreCategoriesContainer,
    {
      className: "store-details-stores-v2__store-product-groups",
      fallbackImage: urls.productCategoryFallbackImage,
      storeCategories
    }
  ), /* @__PURE__ */ React.createElement("div", { className: "store-details-stores-v2__description" }, /* @__PURE__ */ React.createElement(StoreDescriptionContainer, { storeName: name, description }), isFranchiseStore && /* @__PURE__ */ React.createElement(StoreFranchiseIndicatorContainer, null)), opticians && opticians.length > 0 && /* @__PURE__ */ React.createElement(
    StoreOpticiansContainer,
    {
      className: "store-details-stores-v2__store-opticians",
      fallbackImage: urls.offerFallbackImage,
      opticians
    }
  ), brands && brands.length > 0 && /* @__PURE__ */ React.createElement("div", { className: "store-details-stores-v2__brands" }, /* @__PURE__ */ React.createElement(StoreBrandContainer, { brands })), /* @__PURE__ */ React.createElement("div", { className: "store-details-stores-v2__payment-methods-social-media" }, showPaymentMethods && !!logos && /* @__PURE__ */ React.createElement(
    StorePaymentMethodsContainer,
    {
      className: "store-details-stores-v2__payment-methods",
      logos: logos.paymentMethods,
      paymentMethods
    }
  ), showSocialMedia && !!logos && /* @__PURE__ */ React.createElement(
    StoreSocialMediaContainer,
    {
      className: "store-details-stores-v2__social-media",
      logos: logos.socialMedia,
      socialMediaLinks
    }
  )))), /* @__PURE__ */ React.createElement("div", { className: "store-details-stores-v2__side-content" }, /* @__PURE__ */ React.createElement("div", { className: "store-details-stores-v2__detail-image" }, /* @__PURE__ */ React.createElement(
    StoreDetailImageContainer,
    {
      detailImageUrl: detailImage,
      fallbackImage: urls.storeFallbackImage,
      alt: shortName || name
    }
  )), /* @__PURE__ */ React.createElement(StoreRatingContainer, { store }), offers && offers.length > 0 && /* @__PURE__ */ React.createElement(
    StoreOffersContainer,
    {
      className: "store-details-stores-v2__store-offers",
      fallbackImage: urls.offerFallbackImage,
      offers
    }
  ), nearestStores.length > 0 && /* @__PURE__ */ React.createElement(
    NearbyStoresContainer,
    {
      className: "store-details-stores-v2__nearest-stores",
      stores: nearestStores,
      urls,
      storeDetails: store
    }
  ), storeCategories && storeCategories.length > 0 && /* @__PURE__ */ React.createElement(
    StoreCategoriesContainer,
    {
      className: "store-details-stores-v2__store-product-groups",
      fallbackImage: urls.productCategoryFallbackImage,
      storeCategories
    }
  ), showPaymentMethods && logos && /* @__PURE__ */ React.createElement(StorePaymentMethodsContainer, { logos: logos.paymentMethods, paymentMethods }), showSocialMedia && logos && /* @__PURE__ */ React.createElement(StoreSocialMediaContainer, { logos: logos.socialMedia, socialMediaLinks }))), showBookAppointmentCTA && /* @__PURE__ */ React.createElement(
    "div",
    {
      "data-t": "cta-sticky-footer",
      className: classnames("store-details-stores-v2__sticky-footer", {
        "store-details-stores-v2__sticky-footer--hidden": hideStickyBookAppointmentButton
      })
    },
    /* @__PURE__ */ React.createElement(
      StoreBookAppointmentCTAContainer,
      {
        url: urls.bookAppointment ?? "",
        code,
        globalStoreId,
        name,
        town,
        country,
        streetName,
        streetNumber,
        additionalStreetInfo,
        postalCode,
        appointmentTypeId
      }
    )
  ));
};
export {
  StoreDetailsRoot as default
};
//# sourceMappingURL=StoreDetailsRoot.js.map
